import React, { useEffect, useMemo, useRef, useState } from "react";
import Popup from "./Popup";
import {
  DATE_TIME_FORMAT,
  caseStatus,
  caseStatus as caseStatusList,
  fileNameType,
  invoiceStatusColor,
  invoiceStatusLabel,
  invoiceStatus as invoiceStatusValue,
} from "app/constants";
import moment from "moment";
import { commonServices, projectServices } from "app/services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import messages from "../../../../services/api/messages";
import { formatIndex, getBaseName, getFileExtension } from "../../../../utils";
import PrimaryBtn from "../../../UI/Button/PrimaryBtn";
import { Dialog, DialogActions, DialogContent } from "@mui/material";
import InputText from "../../../UI/Form/Input/InputText";
import { useFormik } from "formik";

function InvoiceCell(props) {
  const {
    width,
    invoiceStatus,
    invoiceInfo,
    caseInfo,
    setCaseInfo,
    highlight = false,
    setDownloading,
  } = props;
  const [anchor, setAnchor] = useState(null);
  const { downloadPDFWithFileId } = commonServices;
  const [openModal, setOpenModal] = useState(false);
  const ref = useRef();
  const disabledChangeStatus = useMemo(() => {
    return (
      (caseInfo?.caseStatus === caseStatus.completed ||
        caseInfo?.caseStatus === caseStatus.lost) &&
      invoiceStatus !== invoiceStatusValue.submitted
    );
  }, [caseInfo, invoiceStatus]);
  const isConfirmed = useMemo(() => {
    return invoiceStatus === invoiceStatusValue.confirmed;
  }, [invoiceStatus]);
  const isSubmitted = useMemo(() => {
    return invoiceStatus === invoiceStatusValue.submitted;
  }, [invoiceStatus]);

  useEffect(() => {
    if (highlight) {
      ref?.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [highlight]);

  const handleLeftClickInvoice = () => {
    if (invoiceInfo?.fileInfo || invoiceInfo?.slipExcelFileInfo) {
      const caseId = caseInfo?.caseId || "";
      const billName = fileNameType["invoice"];
      const deliveryName = fileNameType["delivery"];
      const customerName = caseInfo?.customer?.companyNameKanji || "";

      const billFiles = Array.isArray(invoiceInfo?.fileInfo)
        ? invoiceInfo?.fileInfo
        : [invoiceInfo?.fileInfo];

      const deliveryFiles = Array.isArray(invoiceInfo?.slipExcelFileInfo)
        ? invoiceInfo?.slipExcelFileInfo
        : [invoiceInfo?.slipExcelFileInfo];

      setDownloading(true);
      Promise.all([
        handleDownloadFile(billFiles, `${billName}_${caseId}_${customerName}`),

        handleDownloadFile(
          deliveryFiles,
          `${deliveryName}_${caseId}_${customerName}`
        ),
      ])
        .then(() => {
          // if (caseInfo?.caseStatus === caseStatusList.invoice) {
          //   projectServices
          //     .updateCaseStatusComplete(caseInfo._id)
          //     .then(() => {
          //       setCaseInfo({
          //         ...caseInfo,
          //         caseStatus: caseStatusList.completed,
          //       });
          //     })
          //     .catch(() => toast.error(messages.callApiFail));
          // }
          setDownloading(false);
        })
        .catch(() => {
          toast.error(messages.downloadFail);
          setDownloading(false);
        });
    }
  };

  function handleDownloadFile(files, filename) {
    if (!Array.isArray(files) || files.length === 0) {
      return;
    }

    const timeStamp = moment().format("YYYYMMDDHHmmss");

    return Promise.all(
      files.map(
        (file, index) =>
          new Promise((resolve) => {
            setTimeout(() => {
              const name = file.fileName
                ? getBaseName(file.fileName)
                : `${filename}_${formatIndex(index + 1)}_${timeStamp}`;

              const extension = getFileExtension(file);
              if (extension === "zip") {
                commonServices
                  .downloadWithFileId(file._id, extension, name)
                  .then(() => resolve());
              } else {
                downloadPDFWithFileId(file._id, name).then(() => resolve());
              }
            }, index * 500);
          })
      )
    );
  }

  const handleRightClickInvoice = (e) => {
    e.preventDefault();
    if (disabledChangeStatus) return;
    if (isSubmitted || isConfirmed) setAnchor(e.target);
  };

  const showModal = () => {
    setOpenModal(true);
    setAnchor(null);
    resetForm();
  };

  const closeModal = () => {
    setOpenModal(false);
    setAnchor(null);
  };

  const handleUpdateInvoiceStatus = (value) => {
    projectServices
      .updateStatus({
        id: caseInfo._id,
        statusType: "invoiceStatus",
        value: values.comment ? invoiceStatusValue.rejected : value,
        comment: values.comment,
      })
      .then((res) => {
        const newCaseInfo = res?.data?.result;
        if (!newCaseInfo._id) {
          throw Error("Update error!");
        }
        setAnchor(null);
        setOpenModal(false);
        setCaseInfo({
          ...caseInfo,
          invoiceStatus: newCaseInfo?.invoiceStatus,
          updateInvoiceTime: newCaseInfo?.updateInvoiceTime,
          caseStatus: newCaseInfo?.caseStatus,
        });
        toast.success(messages.update(true));
      })
      .catch(() => {
        toast.error(messages.callApiFail);
      });
  };

  const {
    handleSubmit,
    resetForm,
    errors,
    values,
    setFieldValue,
    touched,
    handleBlur,
  } = useFormik({
    initialValues: {
      comment: "",
    },
    onSubmit: handleUpdateInvoiceStatus,
    enableReinitialize: true,
  });

  const renderPopup = () => {
    if (isSubmitted) {
      return (
        <div className="flex flex-col">
          <button
            className="bg-[#c7c2c2] w-7 h-7 rounded-full ml-auto flex items-center justify-center"
            onClick={() => setAnchor(null)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
          <p className="flex gap-2">
            最終更新日:{" "}
            {moment(
              caseInfo?.updateInvoiceTime || invoiceInfo?.updatedAt
            ).format(DATE_TIME_FORMAT)}
          </p>
        </div>
      );
    }
    if (isConfirmed) {
      return (
        <div className="flex gap-2">
          <PrimaryBtn
            text="承認"
            onClick={() =>
              handleUpdateInvoiceStatus(invoiceStatusValue.submitted)
            }
          />
          <PrimaryBtn text="差し戻し" onClick={showModal} />
          <PrimaryBtn text="戻る" onClick={() => setAnchor(null)} />
        </div>
      );
    }
  };

  return (
    <>
      <div
        ref={ref}
        className={`h-full border border-[#B3B3B3] px-2 py-4 shrink-0 ${
          highlight && "bg-yellow-100"
        }`}
        style={{
          width: width,
        }}
      >
        <p className="text-sm leading-[18px] tracking-[.2em] truncate text-center">
          <button
            style={{
              color: invoiceStatusColor[invoiceStatus],
              cursor: "pointer",
            }}
            onClick={handleLeftClickInvoice}
            onContextMenu={handleRightClickInvoice}
          >
            {invoiceStatusLabel[invoiceStatus]}
          </button>
        </p>
      </div>

      <Popup
        id={`invoice-${invoiceInfo?._id}`}
        anchor={anchor}
        open={Boolean(anchor)}
      >
        {renderPopup()}
      </Popup>

      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogContent
          sx={{
            pl: "16px",
          }}
        >
          <div className="mb-4">コメント</div>
          <InputText
            name="comment"
            value={values.comment}
            onChange={(value) => setFieldValue("comment", value)}
            handleBlur={handleBlur}
            errors={errors.comment}
            touched={touched.comment}
          />
        </DialogContent>

        <DialogActions>
          <PrimaryBtn text={"キャンセル"} onClick={closeModal} />
          <PrimaryBtn text={"送信"} onClick={handleSubmit} />
        </DialogActions>
      </Dialog>
    </>
  );
}

export default InvoiceCell;
